/**
 * Renewal Decision Service — API client for renewal decision CRUD.
 *
 * Mirrors the backend RenewalDecisionService endpoints for managing
 * renewal decisions linked to contracts.
 *
 * Requirements: 26.1, 26.2, 26.3, 26.4, 26.5
 */

import { apiClient } from '@/services/apiClient';
import type { RenewalDecision } from '@/types';

// ─── Types ─────────────────────────────────────────────────────────────

export interface CreateDecisionInput {
  decisionStatus: string;
  decisionNotes: string;
}

export interface UpdateDecisionInput {
  decisionStatus?: string;
  decisionNotes?: string;
}

// ─── Service ───────────────────────────────────────────────────────────

export const renewalDecisionService = {
  /** GET /api/contracts/:contractId/renewal-decisions — List decisions for a contract. */
  listDecisions(contractId: number): Promise<RenewalDecision[]> {
    return apiClient.get(`contracts/${contractId}/renewal-decisions`);
  },

  /** GET /api/renewal-decisions/:id — Get a single decision. */
  getDecisionById(id: number): Promise<RenewalDecision> {
    return apiClient.get(`renewal-decisions/${id}`);
  },

  /** POST /api/contracts/:contractId/renewal-decisions — Create a decision. */
  createDecision(contractId: number, input: CreateDecisionInput): Promise<RenewalDecision> {
    return apiClient.post(`contracts/${contractId}/renewal-decisions`, input);
  },

  /** PUT /api/renewal-decisions/:id — Update a decision. */
  updateDecision(id: number, input: UpdateDecisionInput): Promise<RenewalDecision> {
    return apiClient.put(`renewal-decisions/${id}`, input);
  },

  /** DELETE /api/renewal-decisions/:id — Delete a decision. */
  deleteDecision(id: number): Promise<void> {
    return apiClient.delete(`renewal-decisions/${id}`);
  },
};
